import { Form } from 'antd';
import Modal from 'components/Modal';
import Text from 'components/Text';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import colorTheme from 'constants/color';
import TableStaffSale from './TableStaffSale';
import Box from 'components/Box';
import { momentTimezone } from 'utils/time';
import Button from 'components/Button';
import ticketListApis from 'features/ticketList/services/apis';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';

type Ref = {
  open: (createById?: string) => void;
};

type Props = {};

export type StaffSaleItem = {
  staffId: string;
  staffName: string;
  avatar: string;
  service: string;
  serviceId: string;
  amount: number;
};

export type AddStaffSaleFormValues = {
  staffs: StaffSaleItem[];
};

export const useAddStaffSaleModalRef = () => useRef<Ref>(null);

export const AddStaffSaleModal = forwardRef<Ref, Props>(({}, ref) => {
  const dispatch = useAppDispatch();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm<AddStaffSaleFormValues>();
  const details = ticketListSelectors.ticketDetailsData.data();
  const [createById, setCreateById] = useState<string>();

  const handleClose = () => {
    setVisible(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    open(createById?: string) {
      setVisible(true);
      setCreateById(createById);
    },
  }));

  const handleAddStaff = () => {
    const staffs = form.getFieldValue('staffs') || [];

    form.setFieldsValue({
      staffs: [
        ...staffs,
        {
          service: '',
          staffId: undefined,
          amount: 0,
        },
      ],
    });
  };

  const handleSubmit = async (values: AddStaffSaleFormValues) => {
    const body = {
      billId: details?.billId || '',
      createdBy: createById || '',
      items: values.staffs.map((item) => ({
        staffId: item.staffId,
        amount: item.amount,
        serviceId: item.serviceId,
      })),
    };

    try {
      const res: IResponseDataBody = await ticketListApis.addSaleStaff(body);
      if (res.data.data) {
        handleClose();
        toast.success('Sale adjustment successfully');
        dispatch(ticketListActions.getTicketDetails.fetch(details?.billId || ''));
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Sale adjustment failed');
    }
  };

  const staffs = Form.useWatch('staffs', form);

  const errors: string[] = [];

  if (staffs?.some((s) => !s.service)) {
    errors.push('Please select service for all item');
  }

  if (staffs?.some((s) => !s.staffId)) {
    errors.push('Please select staff for all item');
  }
  
  if (staffs?.some((s) => !s.amount || s.amount <= 0)) {
    errors.push('Amount must be greater than 0');
  }

  const disableOk = errors.length > 0;

  return (
    <Modal
      modalTitle='Sale Adjustment'
      okTitle={'Ok'}
      closable={false}
      visible={visible}
      onClose={handleClose}
      width={1200}
      disableOk={disableOk}
      noneBodyStyle
      noneFooterStyle
      className='modal-overflow-unset modal-max-height-unset'
      centered
      forceRender
      v2
      onSubmit={() => {
        form.submit();
      }}
    >
      <Container>
        <Form
          form={form}
          initialValues={{
            staffs: [
              {
                staffId: '',
                staffName: '',
                avatar: '',
                amount: 0,
              },
            ],
          }}
          onFinish={handleSubmit}
        >
          <Title>
            <Box display='flex' gap='2' alignItems='center'>
              <div className='ticket-number'>
                <span>#{details?.ticketNumber}</span>
              </div>
              <Box display='flex' flexDirection='column' gap='1'>
                <Text variant='H6' color='text_4_v2' textAlign='left' style={{ fontWeight: 500 }}>
                  {momentTimezone(details?.startTime).format('DD/MM/YYYY')}{' '}
                </Text>
                <Text variant='H6' color='text_4_v2' textAlign='left' style={{ fontWeight: 500 }}>
                  {momentTimezone(details?.startTime).format('hh:mm A')} -{' '}
                  {momentTimezone(details?.endTime).format('hh:mm A')}
                </Text>
              </Box>
            </Box>
            <Button ntype='GREEN' icon='addWhite' onClick={handleAddStaff} className='add-button'>
              Add Staff
            </Button>
          </Title>
          <TableStaffSale form={form} />
          {errors.length > 0 && (
            <Text color='error_6' textAlign='center' variant='H7' style={{ fontWeight: 500 }} m={1}>
              {errors[0]}
            </Text>
          )}
        </Form>
      </Container>
    </Modal>
  );
});

AddStaffSaleModal.displayName = 'AddStaffSaleModal';

export default AddStaffSaleModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  .currency-container {
    width: 500px;
  }

  .table-wrapper {
    max-height: 500px;
    overflow: auto;
    padding: 0 12px;
    .table-thead {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #ffffff;
    }

    tfoot {
      position: sticky;
      bottom: 0px;
      z-index: 99;
      tr th {
        background: ${colorTheme.v2_neutral_2};
        padding: 8px 12px;
        .ant-btn {
          box-shadow: none;
        }
      }
    }
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;

  .ticket-number {
    display: flex;
    padding: 4px 16px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 4px;
    background: #232f3e;

    span {
      color: #fff;
      font-family: Poppins;
      font-size: 36px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }

  .add-button {
    .CONTENT_2 {
      font-size: 20px;
      font-family: Poppins;
    }
  }
`;
